<script setup lang="ts">
/**
 * Input — text/number/email input.
 *
 * IMPORTANT: This component receives an `error` prop for VISUAL
 * STATE ONLY (red border + aria-invalid). It does NOT render the
 * error message string anywhere. Error text is rendered exactly
 * once by the wrapping <FormRow>.
 *
 * If you find yourself adding a `<p v-if="error">{{ error }}</p>`
 * here, stop — that's how we ended up with the duplicate-error bug
 * in Batch B. The FormRow contract is the one source of truth.
 */
import { computed } from 'vue'

interface Props {
  modelValue?: string | number | null
  type?: string
  placeholder?: string
  error?: string
  disabled?: boolean
  readonly?: boolean
  autofocus?: boolean
  dir?: 'ltr' | 'rtl' | 'auto'
  /** Optional name attribute, useful for autofill heuristics. */
  name?: string
  /** Optional id attribute, paired with FormRow's label. */
  id?: string
}
const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  dir: 'auto',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
}>()

const hasError = computed(
  () => typeof props.error === 'string' && props.error.length > 0,
)

const displayValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return ''
  return String(props.modelValue)
})

function onInput(e: Event): void {
  const target = e.target as HTMLInputElement
  emit('update:modelValue', target.value)
}
</script>

<template>
  <input
    :id="id"
    :name="name"
    :type="type"
    :value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :dir="dir"
    :autofocus="autofocus || undefined"
    :aria-invalid="hasError || undefined"
    :class="[
      'input',
      { 'input--error': hasError, 'input--disabled': disabled },
    ]"
    @input="onInput"
    @blur="emit('blur', $event)"
    @focus="emit('focus', $event)"
  />
  <!--
    Intentionally empty: no error <p> here.
    FormRow renders the error text once, below the field.
  -->
</template>

<style scoped>
.input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-text-1);
  background-color: var(--color-surface-2);
  border: 1px solid var(--color-border-2);
  border-radius: 6px;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.input::placeholder {
  color: var(--color-text-4);
}
.input:focus {
  border-color: var(--color-brand);
  box-shadow: 0 0 0 3px var(--color-brand-soft, rgba(230, 0, 41, 0.18));
}
.input--error {
  border-color: var(--color-danger);
}
.input--error:focus {
  box-shadow: 0 0 0 3px var(--color-danger-soft, rgba(230, 0, 41, 0.18));
}
.input--disabled {
  background-color: var(--color-surface-3);
  color: var(--color-text-4);
  cursor: not-allowed;
}
</style>
